import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Field, Input, Dropdown, Option } from '@fluentui/react-components';
import { TCustomFieldValue } from '@modules/items';
import { TCustomFieldRaw, ECustomFieldType, TCustomField } from '@modules/users/types/organizations.types';
import { SignInContext } from '@modules/users';
import { ApiContext } from '@shared/contexts';
import CalendarDatePicker from '@shared/components/CalendarDatePicker';
import { DEFAULT_OPTION_LABEL } from '@shared/i18n';
import { localDateToYMD, ymdToLocalDate } from '@shared/utils';
import { ButtonLoading } from '@shared/components';
import extractCustomFieldValue from '../../../helpers/extractCustomFieldValue.helper';
import { mergeIncomingValues } from './mergeIncomingValues';

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/organizations/schemas/configs/customFields.schema.ts
const CF_TEXT_MAX_LENGTH = 250;

type TProps = {
  customFieldValues: TCustomFieldValue[] | undefined;
  fallbackToOrgDefault: boolean;
  isEditable: boolean;
  // Rejects when saving fails; the caller's mutation already shows the error toast.
  saveValue: (fieldId: string, value: TCustomFieldRaw) => Promise<unknown>;
  // Unique-id fields show only with it: blocks never get an id, and a template default for one would never be used.
  generateUniqueId?: (cf: TCustomField) => Promise<TCustomFieldRaw>;
};

// Uses Record<string, any> because field IDs are dynamic and each field type
// maps to a different value shape (string, string[], or null).
const getInitialValues = (
  customFields: TCustomField[],
  customFieldValues: TCustomFieldValue[] | undefined,
  fallbackToOrgDefault: boolean
): Record<string, any> => {
  return customFields.reduce(
    (acc, cf) => {
      const existing = customFieldValues?.find((v) => v.customField === cf.id)?.value;
      const raw = existing ?? (fallbackToOrgDefault ? cf.defaultValue : null);
      switch (cf.type) {
        case ECustomFieldType.TEXT:
          acc[cf.id] = extractCustomFieldValue(raw) ?? '';
          break;
        case ECustomFieldType.DROPDOWN: {
          // Empty uses [''] (not []) so the empty sentinel Option stays highlighted.
          const dropdownValue = extractCustomFieldValue(raw);
          acc[cf.id] = dropdownValue ? [dropdownValue] : [''];
          break;
        }
        case ECustomFieldType.DATE:
          acc[cf.id] = extractCustomFieldValue(raw) || null;
          break;
        default:
          throw new Error(`Unsupported custom field type: ${cf.type}`);
      }
      return acc;
    },
    {} as Record<string, any>
  );
};

const toApiValue = (fieldType: ECustomFieldType, value: any): TCustomFieldRaw => {
  switch (fieldType) {
    case ECustomFieldType.TEXT:
      return { TEXT: value };
    case ECustomFieldType.DROPDOWN:
      return { DROPDOWN: value[0] ?? '' };
    case ECustomFieldType.DATE:
      // value is already YYYY-MM-DD string or null
      return { DATE: value ?? '' };
    default:
      throw new Error(`Unsupported custom field type: ${fieldType}`);
  }
};

const withFieldId = (ids: Set<string>, id: string, present: boolean): Set<string> => {
  const next = new Set(ids);
  if (present) next.add(id);
  else next.delete(id);
  return next;
};

const CustomFieldInputs = ({
  customFieldValues,
  fallbackToOrgDefault,
  isEditable,
  saveValue,
  generateUniqueId,
}: TProps) => {
  const { organization } = useContext(SignInContext);
  const { show } = useContext(ApiContext);

  const [values, setValues] = useState<Record<string, any>>(() =>
    getInitialValues(organization.customFields, customFieldValues, fallbackToOrgDefault)
  );
  const originalValues = useRef(values);
  const [savingFieldIds, setSavingFieldIds] = useState<Set<string>>(new Set());
  const [generatingFieldIds, setGeneratingFieldIds] = useState<Set<string>>(new Set());

  // Merge cache updates without discarding a genuinely divergent local edit.
  useEffect(() => {
    const incomingValues = getInitialValues(organization.customFields, customFieldValues, fallbackToOrgDefault);
    const previousOriginalValues = originalValues.current;
    originalValues.current = incomingValues;
    setValues((localValues) => mergeIncomingValues(localValues, previousOriginalValues, incomingValues));
  }, [customFieldValues, organization.customFields, fallbackToOrgDefault]);

  const saveField = async (fieldId: string, fieldType: ECustomFieldType, fieldLabel: string, value: any) => {
    const apiValue = toApiValue(fieldType, value);

    setSavingFieldIds((ids) => withFieldId(ids, fieldId, true));
    try {
      await saveValue(fieldId, apiValue);
      originalValues.current = { ...originalValues.current, [fieldId]: value };
      show('success', `${fieldLabel} saved.`);
    } catch {
      // Error toast is handled by mutation's onError callback
    } finally {
      setSavingFieldIds((ids) => withFieldId(ids, fieldId, false));
    }
  };

  const handleTextBlur = async (event: React.FocusEvent<HTMLInputElement>, cf: TCustomField) => {
    const nextFocusedElement = event.relatedTarget;
    if (nextFocusedElement instanceof HTMLElement && nextFocusedElement.dataset.customFieldGenerateId === cf.id) return;

    const value = values[cf.id];
    if (value === originalValues.current[cf.id]) return;
    // Client-side validation for text length
    if (value && value.length > CF_TEXT_MAX_LENGTH) {
      show('error', `${cf.label} cannot exceed ${CF_TEXT_MAX_LENGTH} characters.`);
      return;
    }
    await saveField(cf.id, ECustomFieldType.TEXT, cf.label, value);
  };

  const handleGenerate = async (cf: TCustomField, generate: (cf: TCustomField) => Promise<TCustomFieldRaw>) => {
    setGeneratingFieldIds((ids) => withFieldId(ids, cf.id, true));
    try {
      const generatedValue = extractCustomFieldValue(await generate(cf)) ?? '';
      setValues((prev) => ({ ...prev, [cf.id]: generatedValue }));
      originalValues.current = { ...originalValues.current, [cf.id]: generatedValue };
      show('success', `${cf.label} generated.`);
    } catch {
      // Error toast is handled by mutation's onError callback
    } finally {
      setGeneratingFieldIds((ids) => withFieldId(ids, cf.id, false));
    }
  };

  const handleTextKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleDropdownChange = async (cf: TCustomField, newValue: string[]) => {
    setValues((prev) => ({ ...prev, [cf.id]: newValue }));
    if (newValue[0] === originalValues.current[cf.id]?.[0]) return;
    await saveField(cf.id, ECustomFieldType.DROPDOWN, cf.label, newValue);
  };

  const handleDateChange = async (cf: TCustomField, date: Date | null | undefined) => {
    // Store as YYYY-MM-DD string (matches API format) to avoid timezone issues
    const newValue = date ? localDateToYMD(date) : null;
    setValues((prev) => ({ ...prev, [cf.id]: newValue }));
    if (newValue === originalValues.current[cf.id]) return;
    await saveField(cf.id, ECustomFieldType.DATE, cf.label, newValue);
  };

  return (
    <>
      {organization.customFields.map((cf) => (
        <Fragment key={cf.id}>
          {cf.type === ECustomFieldType.TEXT && (!cf.uniqueId?.enabled || generateUniqueId) && (
            <Field label={cf.label}>
              <Input
                value={values[cf.id] || ''}
                onChange={(e) => setValues((prev) => ({ ...prev, [cf.id]: e.target.value }))}
                onBlur={(event) => handleTextBlur(event, cf)}
                onKeyDown={handleTextKeyDown}
                disabled={!isEditable || savingFieldIds.has(cf.id) || generatingFieldIds.has(cf.id)}
                placeholder="Enter value"
                contentAfter={
                  cf.uniqueId?.enabled && generateUniqueId ? (
                    <ButtonLoading
                      appearance="transparent"
                      size="small"
                      loading={generatingFieldIds.has(cf.id)}
                      disabled={!isEditable || savingFieldIds.has(cf.id) || generatingFieldIds.has(cf.id)}
                      data-custom-field-generate-id={cf.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        return handleGenerate(cf, generateUniqueId);
                      }}
                    >
                      Generate
                    </ButtonLoading>
                  ) : undefined
                }
              />
            </Field>
          )}

          {cf.type === ECustomFieldType.DROPDOWN && (
            <Field label={cf.label}>
              <Dropdown
                selectedOptions={values[cf.id]?.length ? values[cf.id] : ['']}
                value={values[cf.id]?.length > 0 ? cf.options?.find((o) => o.id === values[cf.id][0])?.label || '' : ''}
                onOptionSelect={(_, data) => {
                  if (data) handleDropdownChange(cf, data.selectedOptions);
                }}
                disabled={!isEditable || savingFieldIds.has(cf.id)}
                placeholder={DEFAULT_OPTION_LABEL}
              >
                <Option key="empty" value="">
                  {DEFAULT_OPTION_LABEL}
                </Option>
                {cf.options?.map((opt) => (
                  <Option key={opt.id} value={opt.id}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}

          {cf.type === ECustomFieldType.DATE && (
            <Field label={cf.label}>
              <CalendarDatePicker
                value={ymdToLocalDate(values[cf.id])}
                onSelectDate={(date) => handleDateChange(cf, date)}
                disabled={!isEditable || savingFieldIds.has(cf.id)}
                placeholder="Pick a date"
              />
            </Field>
          )}
        </Fragment>
      ))}
    </>
  );
};

export default CustomFieldInputs;
